import { ApplicationConfig, provideZoneChangeDetection, importProvidersFrom } from '@angular/core';
import { provideRouter } from '@angular/router';
import { routes } from './app.routes';
import {
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
  HTTP_INTERCEPTORS,
  withInterceptors,
} from '@angular/common/http';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';
import { environment } from '../environments/environment';
import { apiErrorInterceptor } from './interceptors/api-error.interceptor';
import { swPartNumberInterceptor } from './interceptors/sw-part-number.interceptor';
import { loadingInterceptor } from './interceptors/loading.interceptor';
import { prefixWithSlash } from '../shared/internal/prefix-with-slash';
import { ROUTE_PATHS } from '../shared/constants/route-paths';
import { provideHttpCache, withHttpCacheInterceptor } from '@ngneat/cashew';
import { provideHotToastConfig } from '@ngxpert/hot-toast';
import { provideNgxMask } from 'ngx-mask';
import { NgxSpinnerModule } from 'ngx-spinner';

export function loggerCallback(logLevel: LogLevel, message: string): void {
  if (logLevel == LogLevel.Trace || logLevel == LogLevel.Verbose || logLevel == LogLevel.Info) return;

  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      redirectUri: prefixWithSlash(ROUTE_PATHS.signIn),
      postLogoutRedirectUri: prefixWithSlash(ROUTE_PATHS.signedOut),
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[]>();
  // protectedResourceMap.set(
  //   environment.apiConfig.uri,
  //   environment.apiConfig.scopes
  // );
  protectedResourceMap.set(environment.apiConfig.uri, environment.apiConfig.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: prefixWithSlash(ROUTE_PATHS.signInFailed),
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    importProvidersFrom(NgxSpinnerModule.forRoot()),
    provideAnimations(),
    provideNgxMask(),
    provideHttpClient(
      withInterceptorsFromDi(),
      withFetch(),
      withInterceptors([apiErrorInterceptor, swPartNumberInterceptor, loadingInterceptor, withHttpCacheInterceptor()])
    ),
    provideHttpCache(),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideHotToastConfig({ theme: 'minimal', position: 'top-right' }),
  ],
};
